import type { Codec } from '@bomdisoft/store/persisted/types';

export const custom = <T>(parse: (raw: string) => T | undefined, format: (value: T) => string): Codec<T> => ({ format, parse });

export const enumOf = <T extends string>(values: readonly T[]): Codec<T> => ({
  format: value => value,
  parse: raw => (values.includes(raw as T) ? (raw as T) : undefined),
});

export const text = (): Codec<string> => ({
  format: value => value,
  parse: raw => (raw === '' ? undefined : raw),
});

export const boolean = (): Codec<boolean> => ({
  format: value => (value ? 'true' : 'false'),
  parse: raw => (raw === 'true' ? true : raw === 'false' ? false : undefined),
});

export const integer = ({ max, min }: { max?: number; min?: number } = {}): Codec<number> => ({
  format: value => String(value),
  parse: raw => {
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isInteger(parsed) || String(parsed) !== raw.trim()) return undefined;
    if (min !== undefined && parsed < min) return undefined;
    if (max !== undefined && parsed > max) return undefined;
    return parsed;
  },
});

export const nullable = <T>(of: Codec<T>): Codec<T | null> => ({
  format: value => (value === null ? '' : of.format(value)),
  parse: raw => (raw === '' ? null : of.parse(raw)),
});

export const list = <T>(of: Codec<T>): Codec<T[]> => ({
  format: value => JSON.stringify(value.map(entry => of.format(entry))),
  parse: raw => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return undefined;
    }
    if (!Array.isArray(parsed)) return undefined;
    const items: T[] = [];
    for (const entry of parsed) {
      if (typeof entry !== 'string') return undefined;
      const item = of.parse(entry);
      if (item === undefined) return undefined;
      items.push(item);
    }
    return items;
  },
});

export const record = <T extends Record<string, unknown>>(fields: { [K in keyof T]: Codec<T[K]> }, defaults: T): Codec<T> => ({
  format: value => JSON.stringify(Object.fromEntries(Object.keys(fields).map(name => [name, fields[name as keyof T].format(value[name as keyof T])]))),
  parse: raw => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return undefined;
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
    const source = parsed as Record<string, unknown>;
    const result = { ...defaults };
    for (const name of Object.keys(fields) as (keyof T)[]) {
      const entry = source[name as string];
      if (typeof entry !== 'string') continue;
      const value = fields[name].parse(entry);
      if (value !== undefined) result[name] = value;
    }
    return result;
  },
});
